import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'x-access-token';
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(
    this.hasToken(),
  );
  private currentUserSubject = new BehaviorSubject<User | null>(
    this.getUserFromStorage(),
  );

  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private router: Router) {
    // Check authentication status on service initialization
    this.checkAuthStatus();
  }

  /**
   * Login user with credentials
   */
  login(credentials: LoginCredentials): Observable<any> {
    return new Observable((observer) => {
      // Simulate API call
      setTimeout(() => {
        if (this.validateCredentials(credentials)) {
          const user: User = {
            id: '1',
            email: credentials.email,
            name: 'User Name',
          };

          // Set authentication token
          this.setToken('true');

          // Store user data
          this.setUser(user);

          // Update authentication state
          this.isAuthenticatedSubject.next(true);
          this.currentUserSubject.next(user);

          observer.next({ success: true, user });
          observer.complete();
        } else {
          observer.error({ message: 'Invalid credentials' });
        }
      }, 1000);
    });
  }

  /**
   * Logout user
   */
  logout(): void {
    this.removeToken();
    this.removeUser();
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  /**
   * Reset password
   */
  resetPassword(email: string): Observable<any> {
    return new Observable((observer) => {
      // Simulate API call
      console.log('Resetting password for:', email);
      setTimeout(() => {
        observer.next({ success: true, message: 'Reset password email sent' });
        observer.complete();
      }, 1000);
    });
  }

  /**
   * Forgot password
   */
  forgotPassword(email: string): Observable<any> {
    return new Observable((observer) => {
      // Simulate API call
      console.log('Sending forgot password email to:', email);
      setTimeout(() => {
        observer.next({
          success: true,
          message: 'Password reset instructions sent to your email',
        });
        observer.complete();
      }, 1000);
    });
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.hasToken();
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Private methods
   */
  private validateCredentials(credentials: LoginCredentials): boolean {
    return !!(
      credentials.email &&
      credentials.password &&
      credentials.password.length >= 6
    );
  }

  private hasToken(): boolean {
    const token = localStorage.getItem(this.TOKEN_KEY);
    return token === 'true';
  }

  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  private setUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  private removeUser(): void {
    localStorage.removeItem('user');
  }

  private getUserFromStorage(): User | null {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  }

  private checkAuthStatus(): void {
    if (this.hasToken()) {
      const user = this.getUserFromStorage();
      if (user) {
        this.isAuthenticatedSubject.next(true);
        this.currentUserSubject.next(user);
      } else {
        // Token exists but no user data, logout
        this.logout();
      }
    }
  }
}
